import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight, Tag, TicketPercent } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { inr, FREE_SHIPPING_ABOVE } from "@/lib/api";

export default function CartDrawer() {
  const { items, isOpen, closeCart, setQty, remove, subtotal, count, distinct, bundleRate, bundleDiscount, coupon, couponDef, couponDiscount, shippingWaived, applyCoupon, removeCoupon, discount } = useCart();
  const pct = shippingWaived ? 100 : Math.min(100, Math.round((subtotal / FREE_SHIPPING_ABOVE) * 100));
  const remaining = Math.max(0, FREE_SHIPPING_ABOVE - subtotal);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-forest/50 backdrop-blur-sm" onClick={closeCart} data-testid="cart-drawer-overlay">
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="cart-drawer"
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-cream shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-sand px-6 py-4">
              <h2 className="font-display text-xl font-extrabold text-forest">Your cart <span className="text-leaf">({count})</span></h2>
              <button data-testid="cart-drawer-close" onClick={closeCart} aria-label="Close cart" className="flex h-9 w-9 items-center justify-center rounded-full border border-sand bg-white text-forest">
                <X className="h-4 w-4" />
              </button>
            </div>

            {items.length > 0 && (
              <div className="border-b border-sand bg-white px-6 py-3">
                <p className="text-xs font-semibold text-ink/70" data-testid="cart-shipping-message">
                  {shippingWaived || remaining <= 0 ? <span className="text-leaf">Free shipping unlocked</span> : <><span className="text-leaf">{inr(remaining)}</span> away from free shipping</>}
                </p>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sand">
                  <motion.div className="h-full rounded-full bg-leaf" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} data-testid="cart-free-shipping-progress" />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <p className="font-display text-2xl font-extrabold text-forest">Empty. Suspiciously empty.</p>
                  <p className="text-sm text-ink/60">12 powders are waiting to make cooking ridiculously easy.</p>
                  <Link to="/shop" data-testid="cart-empty-shop-link" onClick={closeCart} className="mt-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream transition-colors hover:bg-leaf">
                    Shop the powders
                  </Link>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((i) => (
                    <li key={i.slug} data-testid={`cart-item-${i.slug}`} className="flex gap-3 rounded-2xl border border-sand bg-white p-3">
                      <img src={i.image} alt={i.name} className="h-20 w-20 rounded-xl object-cover" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-display text-sm font-extrabold text-forest">{i.name}</p>
                            <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: i.accent }}>{i.mascot}</p>
                          </div>
                          <button data-testid={`cart-item-remove-${i.slug}`} onClick={() => remove(i.slug)} aria-label={`Remove ${i.name}`} className="text-ink/40 transition-colors hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center rounded-full border border-sand">
                            <button data-testid={`cart-item-minus-${i.slug}`} onClick={() => setQty(i.slug, i.qty - 1)} className="px-2.5 py-1.5 text-forest"><Minus className="h-3.5 w-3.5" /></button>
                            <span data-testid={`cart-item-qty-${i.slug}`} className="w-7 text-center font-mono text-xs font-bold">{i.qty}</span>
                            <button data-testid={`cart-item-plus-${i.slug}`} onClick={() => setQty(i.slug, i.qty + 1)} className="px-2.5 py-1.5 text-forest"><Plus className="h-3.5 w-3.5" /></button>
                          </div>
                          <p className="font-display text-sm font-extrabold text-forest">{inr(i.price * i.qty)}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-sand bg-white px-6 py-4">
                {distinct < 3 && !coupon && (
                  <p className="rounded-xl bg-leaf/10 px-4 py-2.5 text-xs font-bold text-leaf" data-testid="cart-bundle-nudge">
                    Pick {3 - distinct} more {3 - distinct === 1 ? "powder" : "powders"} to unlock 10% off — automatic, no code.
                  </p>
                )}
                {coupon ? (
                  <div className="flex items-center justify-between rounded-xl border border-leaf/30 bg-leaf/10 px-4 py-2.5" data-testid="cart-coupon-applied">
                    <p className="flex items-center gap-2 text-xs font-bold text-leaf">
                      <TicketPercent className="h-4 w-4" /> {coupon} · {couponDef?.label}
                    </p>
                    <button data-testid="cart-coupon-remove" onClick={removeCoupon} className="text-[11px] font-bold uppercase text-ink/50 hover:text-red-500">Remove</button>
                  </div>
                ) : (
                  <form
                    className="mt-2 flex gap-2"
                    onSubmit={(e) => { e.preventDefault(); applyCoupon(e.currentTarget.elements.code.value); e.currentTarget.reset(); }}
                  >
                    <div className="relative flex-1">
                      <Tag className="absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" />
                      <input
                        data-testid="cart-coupon-input"
                        name="code"
                        placeholder="Coupon code (try FIRSTPOWDER)"
                        className="w-full rounded-full border border-sand bg-cream/60 py-2.5 pl-9 pr-3 text-xs font-bold uppercase tracking-wide text-ink placeholder:normal-case placeholder:font-semibold placeholder:text-ink/40 focus:border-leaf"
                      />
                    </div>
                    <button data-testid="cart-coupon-apply" className="rounded-full bg-forest px-4 text-xs font-extrabold uppercase text-cream hover:bg-leaf">Apply</button>
                  </form>
                )}

                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-ink/60">Subtotal</span>
                    <span className="font-bold text-forest">{inr(subtotal)}</span>
                  </div>
                  {bundleDiscount > 0 && (
                    <div className="flex items-center justify-between text-leaf">
                      <span className="font-semibold">Bundle saving ({bundleRate}%)</span>
                      <span data-testid="cart-drawer-bundle-discount" className="font-bold">-{inr(bundleDiscount)}</span>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div className="flex items-center justify-between text-leaf">
                      <span className="font-semibold">{coupon} ({couponDef?.value}%)</span>
                      <span data-testid="cart-drawer-coupon-discount" className="font-bold">-{inr(couponDiscount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-display text-base font-extrabold text-forest">Total</span>
                    <span data-testid="cart-drawer-subtotal" className="font-display text-xl font-extrabold text-forest">{inr(subtotal - discount)}</span>
                  </div>
                </div>
                <Link
                  to="/checkout"
                  data-testid="cart-drawer-checkout-button"
                  onClick={closeCart}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-leaf py-4 font-extrabold text-white transition-all hover:bg-forest active:scale-[0.98]"
                >
                  Checkout <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
